import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import { useStoreState, useStoreActions } from "easy-peasy";
import { TwoColumnLayout } from "../templates";
import { notifyError, notifySuccess } from "../lib/notifications";
import { settings, animations } from "../settings";
import { createPasswordChallenge } from "../api/create-password-challenge";
import { redirectAfterLogin } from "../lib/redirect-after-login";
import {
  PasswordWithValidation,
  PasswordConfirmation,
  ChoosePasswordPanel,
} from "../components";
import { language } from "../settings/language";

export const SetNewPassword = () => {
  const history = useHistory();
  const challenge = useStoreState((state) => state.challenge);
  const setIsAuthenticated = useStoreActions((actions) => actions.setIsAuthenticated);
  const setChallenge = useStoreActions((actions) => actions.setChallenge);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordValid, setPasswordValid] = useState(false);
  const [compromisedPassword, setCompromisedPassword] = useState(null);
  const passwordCompromised = compromisedPassword !== null && password === compromisedPassword;
  const [isLoading, setIsLoading] = useState(false);
  const { animate } = settings;
  const { form } = animations;

  useEffect(() => {
    if (!challenge) {
      history.push("/login");
      return;
    }
    if (challenge.name === "SOFTWARE_TOKEN_MFA") {
      history.push("/verify-2fa");
      return;
    }
    if (challenge.name !== "NEW_PASSWORD_REQUIRED") {
      history.push("/login");
    }
  }, [challenge, history]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (challenge && challenge.name === "NEW_PASSWORD_REQUIRED") {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [challenge]);

  const passwordMatchConfirmation = () => password === passwordConfirmation && passwordConfirmation.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!passwordValid) {
      return notifyError("Please ensure your password meets all requirements");
    }

    if (!passwordMatchConfirmation()) {
      return notifyError("Passwords do not match");
    }

    if (!challenge || challenge.name !== "NEW_PASSWORD_REQUIRED") {
      return notifyError("Session expired. Please log in again.");
    }

    setIsLoading(true);

    try {
      const body = {
        username: challenge.username,
        password,
        session: challenge.session,
        ...(challenge.previous_password_hmac && { previous_password_hmac: challenge.previous_password_hmac }),
      };

      const res = await createPasswordChallenge(body);

      if (res.data.challenge) {
        setChallenge(res.data.challenge);
        setIsLoading(false);
        return;
      }

      setIsAuthenticated(true);
      notifySuccess("Password set successfully");
      redirectAfterLogin(history);
    } catch (error) {
      setIsLoading(false);
      if (!error.response) {
        notifyError("Network error");
      } else if (
        error.response.status === 422 &&
        error.response.data?.error === "password_compromised"
      ) {
        setCompromisedPassword(password);
        // TODO(BHA-386): final copy pending product/design
        notifyError(language("passwordCompromised"));
      } else if (error.response.data && error.response.data.message) {
        notifyError(error.response.data.message);
      } else {
        notifyError("Something went wrong. Please try again.");
      }
    }
  };

  if (!challenge || challenge.name !== "NEW_PASSWORD_REQUIRED") return null;

  return (
    <>
      <Helmet>
        <title>Choose a new password</title>
      </Helmet>

      <TwoColumnLayout leftContent={<ChoosePasswordPanel />}>
        <div className="flex flex-col h-full">
          <div className="flex-1 flex flex-col items-center justify-center">
            <motion.div
              variants={form}
              initial={animate ? "hidden" : ""}
              animate={animate ? "show" : ""}
              className="w-full max-w-md"
            >
              <div className="mb-6">
                <h1 className="heading-responsive font-medium text-black tracking-tight mb-2 font-rubik" style={{ fontWeight: 700, fontSize: "30px", lineHeight: "36px" }}>
                  Choose a new password
                </h1>
                {challenge.reason === "password_expired" && (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
                    Your password has expired. Please set a new password to continue.
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-6 items-start w-full">
                <div className="flex flex-col gap-3 items-start w-full">
                  <div className="w-full">
                    <PasswordWithValidation
                      password={password}
                      setPassword={setPassword}
                      setPasswordValid={setPasswordValid}
                      label="Enter new password"
                      passwordCompromised={passwordCompromised}
                    />
                  </div>

                  <div className="w-full">
                    <PasswordConfirmation
                      password={password}
                      passwordConfirmation={passwordConfirmation}
                      setPasswordConfirmation={setPasswordConfirmation}
                      label="Password confirmation"
                      passwordCompromised={passwordCompromised}
                    />
                  </div>

                  <div className="w-full">
                    <p className="text-sm login-text-muted font-inter leading-5">
                      Your password should be at least 8 characters long and include 1 upper case letter and 1 number. There can be no blank spaces at the beginning or end of the password.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={
                      isLoading || !passwordValid ||
                      !passwordMatchConfirmation() || passwordCompromised
                    }
                    className="w-full login-button rounded-md login-bg-primary text-white text-sm font-normal flex items-center justify-center font-inter disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Saving..." : "Save password"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </TwoColumnLayout>
    </>
  );
};
